import { CerrixPromptService } from "./../../../../../shared/services/cerrix-prompt.service";
import { Component, inject } from "@angular/core";
import { ActiveToast, ToastrService } from "ngx-toastr";
import { DevManagementDataService } from "@app/admin/services/devmanagement.service";
import { Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/admin/i18n/scope";

@Component({
    selector: "dev-actions",
    templateUrl: "./dev-actions.component.html",
    styleUrls: ["./dev-actions.component.scss"],
    imports: [TranslatePipe],
    viewProviders: [provideTranslationScope(scope)],
})
export class DevActionsComponent {
    private dataService = inject(DevManagementDataService);
    private promptService = inject(CerrixPromptService);
    private _toastr = inject(ToastrService);
    private translation = injectTranslation(scope);

    private prompt;
    private toast: ActiveToast<any>;

    private async visualizeAction(
        action: Observable<any>,
        progressMessageKey: string,
        completedMessageKey: string,
    ) {
        await this.translation.ready();
        if (this.prompt) {
            this._toastr.warning(this.translation.t("devActions.actionAlreadyRunning"));
            return;
        }

        if (this.toast) {
            this.toast.portal.destroy();
            this.toast = null;
        }

        this.prompt = this.promptService.loader(this.translation.t(progressMessageKey));
        action
            .pipe(
                finalize(() => {
                    this.prompt.close();
                    this.prompt = null;
                }),
            )
            .subscribe(() => {
                this.toast = this._toastr.success(this.translation.t(completedMessageKey));
            });
    }

    reloadSiteConfigurations() {
        const action = this.dataService.reloadSiteConfigurations();
        this.visualizeAction(
            action,
            "devActions.reloadingSiteConfiguration",
            "devActions.siteConfigurationsReloaded",
        );
    }

    reloadDbSettings() {
        const action = this.dataService.reloadDbSettings();
        this.visualizeAction(action, "devActions.reloadingDbSettings", "devActions.dbSettingsReloaded");
    }

    triggerNotifications() {
        const action = this.dataService.triggerNotifications();
        this.visualizeAction(
            action,
            "devActions.triggeringNotifications",
            "devActions.notificationsTriggered",
        );
    }

    testScheduler() {
        const action = this.dataService.testScheduler();
        this.visualizeAction(
            action,
            "devActions.triggeringTestScheduler",
            "devActions.testSchedulerTriggered",
        );
    }

    clearFluentIconsCache() {
        const action = this.dataService.clearFluentIconsCache();
        this.visualizeAction(
            action,
            "devActions.clearingFluentIconsCache",
            "devActions.fluentIconsCacheCleared",
        );
    }

    controlExecutionGenerateSourceDocs() {
        const action = this.dataService.controlExecutionGenerateSourceDocs();
        this.visualizeAction(
            action,
            "devActions.generatingSourceDocuments",
            "devActions.sourceDocumentsGenerated",
        );
    }

    async migrateToIncidents() {
        await this.translation.ready();
        const startMigration = await this.confirmMigrationToIncidents();
        if (startMigration) {
            await this.dataService.migrateToIncidents();

            this._toastr.success(this.translation.t("devActions.migrationToIncidentsCompleted"));
        } else {
            this._toastr.info(this.translation.t("devActions.migrationToIncidentsCancelled"));
        }
    }

    async confirmMigrationToIncidents(): Promise<boolean> {
        await this.translation.ready();
        if (
            !(await this.promptService
                .confirm(
                    this.translation.t("devActions.migrateToIncidentsConfirmTitle"),
                    this.translation.t("devActions.migrateToIncidentsConfirmMessage"),
                )
                .toPromise())
        ) {
            return false;
        }

        let confirmationNumber = this.getRandomInt(10000, 99999);
        let validation = await this.promptService
            .prompt({
                maxHeight: "375px",
                maxWidth: "440px",
                data: {
                    title: this.translation.t("devActions.migrateToIncidentsSecurityCheckTitle"),
                    message: this.translation.t("devManagement.securityCheckMessage", {
                        number: String(confirmationNumber),
                    }),
                    fields: [
                        {
                            fieldType: GenericListFieldType.Number,
                            fieldName: "number",
                            prettyName: this.translation.t("devManagement.numberField"),
                            defaultValue: 10000,
                            minimumValue: 10000,
                            maximumValue: 99999,
                            required: true,
                        },
                    ],
                },
            })
            .toPromise();

        if (!validation) {
            return false;
        }

        let number = validation.number;
        if (number != confirmationNumber) {
            this._toastr.warning(this.translation.t("devManagement.invalidValidationNumber"));
            return false;
        }

        return true;
    }

    getRandomInt(min: number, max: number) {
        let x = max - min;
        let rnd = Math.floor(Math.random() * x);
        return rnd + min;
    }
}
